import _ from 'lodash'
import { getFrameworkServiceUrl, getFrameworkVar, hasMicroApp, indexAppManifests, isAppCanBeLaunched, isOidcClient, launchMultiTenantView } from '../../common/utils/Sparky'
import { getUpdated } from './reducer'
import {
  APP_ID_PRISMA_ACCESS,
  APP_ID_SCM,
  ASC_PORTAL_MICRO_APP,
  ASC_URL,
  GOV_REGION,
  MSP_MICRO_APP,
  MSP_URL,
  TSG_NONE,
} from '../../common/constants/GlobalConstants'
import { AIOPS_APP_ID, AIOPS_APP_ID_FREE, SUPER_ROOT_ID } from '../../activation/constants'
import { isInProgressStatus, isRunningStatus, showMspPortalInTsg } from '../../hub/utils/common'
import { getAppsFilterFn } from '../../hub/components/common/hubUtils'
import { ASC, SWITCH_MULTI_TENANT_PORTAL } from '../../common/constants/LabelConstants'

import type { AppManifests, AuthState, Instance, ReadonlyTsgTree, Tsg } from '@sparky/framework'
import type { StateRef, SwitcherStoreState, TsgAppInstances } from './store'

export const MSP_PORTAL = 'msp_portal'
export const ASC_PORTAL = 'asc_portal'
export { AIOPS_APP_ID, AIOPS_APP_ID_FREE, APP_ID_PRISMA_ACCESS }

export const inMspPath = () => window.location.pathname.startsWith(MSP_URL) && hasMicroApp(MSP_MICRO_APP)
export const inAscPath = () => window.location.pathname.startsWith(ASC_URL) && hasMicroApp(ASC_PORTAL_MICRO_APP)
export const displayAppUrlPrefix = () => ((inMspPath() && MSP_URL) || (inAscPath() && ASC_URL) || '')

export const getCurrentPortalDisplayAppId = (): string | undefined => {
  if (getFrameworkVar('hub')) {
    return 'hub'
  }
  if (getFrameworkVar('aiops_free')) {
    return AIOPS_APP_ID_FREE
  }
  if (inMspPath()) {
    return MSP_PORTAL
  }
  if (inAscPath()) {
    return ASC_PORTAL
  }
  const isSCM = isOidcClient(getFrameworkVar('netsec_client_id') || 'scm')
  if (isSCM) {
    return getFrameworkVar('merge_oneapp_tiles') ? APP_ID_SCM : AIOPS_APP_ID
  }
  if (getFrameworkVar('sase')) {
    return APP_ID_PRISMA_ACCESS
  }
}

export const getAvailableApps = (apps: AppManifests | undefined, user: AuthState['user']) => {
  const filteredApps = apps?.filter(getAppsFilterFn(user))
  const hub = apps?.get('hub')
  if (filteredApps && hub) {
    filteredApps.push(hub)
  }
  return indexAppManifests(filteredApps)
}

export type GetSortedAppInstancesParams = Pick<SwitcherStoreState, 'tsg' | 'contextTsg' | 'instances' | 'availableApps' | 'isSingleTenantOnly' | 'currentPortalDisplayAppId' | 'hasAscRole'>
export const getSortedAppInstances = ({
  tsg,
  contextTsg,
  instances,
  availableApps,
  currentPortalDisplayAppId = getCurrentPortalDisplayAppId(),
  isSingleTenantOnly,
  hasAscRole,
}: GetSortedAppInstancesParams): TsgAppInstances | undefined => {
  if (!tsg || !instances || !availableApps) {
    return undefined
  }
  const isCurrentTsg = Boolean(contextTsg && tsg.id === contextTsg.id)
  const scmPathOverride = getFrameworkVar('url_overriding')?.[APP_ID_SCM]
  const mappedInstances = instances.reduce((all, instance) => {
    const app = availableApps.get(instance.app_id)
    if (app?.enabled && instance && !instance.extra?.hidden && !instance.runtime_attributes?.hidden &&
      ((instance.url && isRunningStatus(instance)) || isInProgressStatus(instance)) &&
      !app.extension_to && !app.no_app_tile_ui &&
      (app.is_panw ? app.iam_controlled || app.flags?.iam_controlled : app.is_third_party)
    ) {
      const inProgress = isInProgressStatus(instance)
      const canLaunch = isAppCanBeLaunched(instance)
      const displayAppId = instance.app_launch_display_override || instance.app_id_display_override
      const appWithOverride = (displayAppId !== instance.app_id && availableApps.get(displayAppId)) || app
      const isCurrent = Boolean(canLaunch && isCurrentTsg && currentPortalDisplayAppId &&
        appWithOverride.app_id === currentPortalDisplayAppId)
      const inst = Object.assign(Object.create(instance), {
        inProgress,
        canLaunch,
        isCurrent,
        app: appWithOverride,
        tsg_name: tsg.display_name,
      }, instance.app_launch_url_override && { url: instance.app_launch_url_override })
      if (!instance.app_launch_url_override && displayAppId === APP_ID_SCM && scmPathOverride) {
        // TODO: remove once framework with app_launch_url_override deployed to prod
        const baseUrl = scmPathOverride.base_url?.startsWith('https:') ?
          scmPathOverride.base_url : getFrameworkServiceUrl(scmPathOverride.base_url)
        const url = new URL(scmPathOverride.default_route || '/home', baseUrl || inst.url)
        url.searchParams.set('tsg_id', tsg.id)
        const urlStr = url.toString()
        if (inst.url !== urlStr) {
          inst.url = urlStr
        }
      }
      all.push(inst)
    }
    return all
  }, [] as Instance[])
  const sortByFields = [
    (instance: Instance) => {
      if (instance.inProgress) {
        return 9
      }
      if (currentPortalDisplayAppId) {
        if (instance.app.app_id === currentPortalDisplayAppId) {
          return 0
        }
        const SCMS = [MSP_PORTAL, APP_ID_SCM]
        if (SCMS.includes(currentPortalDisplayAppId) && SCMS.includes(instance.app.app_id)) {
          return 1
        }
      }
      return instance.canLaunch ? 2 : 3
    },
    'app.display_name',
  ] as any[]
  const fawkesApp = availableApps.get(APP_ID_PRISMA_ACCESS)
  const scmApp = availableApps.get(APP_ID_SCM)
  const govRegion = instances.some(inst => ((inst.platform_region || inst.region) === GOV_REGION))
  const isGovSase = govRegion || (instances.length === 0 && tsg?.isSASE)
  const mspUrl = (isGovSase && getFrameworkServiceUrl('msp_portal_gov')) ||
    getFrameworkServiceUrl(MSP_PORTAL) || fawkesApp?.extra?.msp_portal_url || fawkesApp?.extra?.sase_portal_url
  const mspBaseApp = isGovSase ? fawkesApp : scmApp
  if (showMspPortalInTsg(tsg, instances) && mspUrl && mspBaseApp) {
    const url = new URL(MSP_URL, mspUrl)
    url.searchParams.append('tsg_id', tsg.id)
    const isInMsp = currentPortalDisplayAppId === MSP_PORTAL
    mappedInstances.push({
      app_id: MSP_PORTAL,
      tsg_id: tsg.id,
      tenant_id: '',
      support_account_id: '',
      url: url.toString(),
      canLaunch: true, // hasMicroApp(MSP_MICRO_APP),
      isCurrent: isCurrentTsg && isInMsp,
      launch: () => {
        const { pathname, search } = window.location
        const redirect = pathname.startsWith(MSP_URL) ? `${pathname}${search}` : MSP_URL
        launchMultiTenantView(tsg.id, redirect)
        // return false to not hide
      },
      app: {
        ...mspBaseApp,
        app_id: MSP_PORTAL,
        display_name: mspBaseApp?.extra?.multi_tenant_display_name || SWITCH_MULTI_TENANT_PORTAL,
      },
    })
  }
  if (hasAscRole && mspBaseApp) {
    const ascBaseUrl = getFrameworkServiceUrl(ASC_PORTAL)
    const url = ascBaseUrl ? new URL(ascBaseUrl) : new URL(ASC_URL, window.location.origin)
    url.searchParams.append('tsg_id', tsg.id)
    const isInAsc = currentPortalDisplayAppId === ASC_PORTAL
    mappedInstances.push({
      app_id: ASC_PORTAL,
      tsg_id: tsg.id,
      tenant_id: '',
      support_account_id: '',
      url: url.toString(),
      canLaunch: true,
      isCurrent: isCurrentTsg && isInAsc,
      launch: () => {
        launchMultiTenantView(tsg.id, ASC_URL)
        // return false to not hide
      },
      app: {
        ...mspBaseApp,
        app_id: ASC_PORTAL,
        display_name: ASC,
      }
    })
  }
  const sorted = _.orderBy<Instance>(mappedInstances, sortByFields)
  Object.defineProperties(sorted, {
    tsg: { value: tsg }
  })
  if (!sorted.length && !isSingleTenantOnly) {
    return sorted as TsgAppInstances
  }
  const hub = availableApps.get('hub')
  const hubUrl = getFrameworkServiceUrl('app_portal')
  if (!getFrameworkVar('hub') && hub && hubUrl) {
    const url = new URL('/hub', hubUrl)
    url.searchParams.append('tsg_id', tsg.id)
    sorted.push({
      app_id: hub.app_id,
      tsg_id: tsg.id,
      tenant_id: '',
      support_account_id: '',
      url: url.toString(),
      addIconCls: 'cm-p-1',
      canLaunch: false,
      app: hub,
    })
  }
  return sorted as TsgAppInstances
}

export const getUpdatedState = (auth: AuthState, stateRef?: StateRef): SwitcherStoreState => {
  const { tsg_id: contextTsgId = '', instances: globalInstances, tsgs, apps, user } = auth
  const disabled = contextTsgId === TSG_NONE.id || !Boolean(contextTsgId)
  const contextTsg = tsgs?.get(contextTsgId)

  const update = {
    contextTsg,
    disabled,
    tsgs,
    apps,
    isSingleTenantOnly: tsgs.count === 1,
    globalInstances,
  } as SwitcherStoreState
  if (!stateRef) { // init time
    const newState = {
      ...update,
      tsg: contextTsg,
      instances: globalInstances,
      isHub: Boolean(getFrameworkVar('hub')),
      hasAscPortal: hasMicroApp(ASC_PORTAL_MICRO_APP),
      currentPortalDisplayAppId: getCurrentPortalDisplayAppId(),
    }
    newState.availableApps = getAvailableApps(apps, user)
    newState.tsgAppInstances = getSortedAppInstances(newState)
    return newState
  }
  const updated = getUpdated(update, stateRef.current) as SwitcherStoreState
  if (updated?.apps) {
    updated.availableApps = getAvailableApps(apps, user)
    updated.tsgAppInstances = getSortedAppInstances({
      ...stateRef.current,
      ...updated,
    })
  }
  return updated
}

export const renderTsgPath = (tsg: Tsg, tsgs: ReadonlyTsgTree) => {
  if (!tsgs || !tsg.parent_id || tsg.parent_id === SUPER_ROOT_ID) {
    return null
  }
  const tsgList = [] as string[]
  let curTsg = tsg
  while (curTsg.parent_id) {
    tsgList.unshift(curTsg.display_name)
    const parentTsg = tsg.parent_id !== SUPER_ROOT_ID && tsgs.get(curTsg.parent_id)
    if (!parentTsg) {
      break
    }
    curTsg = parentTsg
  }
  if (tsgList.length > 1) {
    return tsgList.join(' / ')
  }
  return null
}
